// @flow
import type { Sample, DataField } from "../../lib/Types";
import React, { Fragment } from "react";
import SampleStore from "../../lib/SampleStore";
import _ from "underscore";
import SampleGroup from "./SampleGroup";
import Button from "../Button";
import { Observer, observer } from "mobx-react";
import styled, { css } from "styled-components";
import { Table, Thead, TableContainer, HeaderText } from "./styles/Theme";
import { closeActive as closeActiveDatePicker } from "./inputs/date_picker_manager";
import { handleAltArrowGridNavigation } from "./sample_grid_keyboard_navigation";

const Notice = styled.div`
  color: #d82a2a;
  font-weight: bold;
`;

type Props = {
  samples: Array<Sample>,
  handleSubmit: (Array<Sample>, string) => void,
  buttonTitle: string,
  showReason: boolean,
  showPlaceholder: ?boolean,
  company_id: number,
  isSubmitting: boolean,
};

type State = {
  samples: Array<Sample>,
  sampleStore: SampleStore,
  groupBy: string,
  reason: string,
  focusedSample: Sample,
  openSample: Sample,
};

class SampleEditor extends React.Component<Props, State> {
  gridRef: any;

  state = {
    samples: [],
    sampleStore:
      this.props.sampleStore ||
      new SampleStore(this.props.samples, "sample_information", this.props.company_id),
    groupBy: "sample_information",
    client_suite_id: null,
    showReason: false,
    reason: "",
    focusedSample: null,
    openSample: null,
  };

  static defaultProps = {
    showPlaceholder: false,
  };

  constructor(props: Props) {
    super(props);
    this.gridRef = React.createRef();
  }

  focusSample = (sample: Sample) => {
    this.setState({ focusedSample: sample });
  };

  openSample = (sample: Sample) => {
    this.setState({ openSample: this.state.openSample === sample ? null : sample });
  };

  openNextSample = () => {
    const { openSample } = this.state;
    if (openSample) {
      var index = this.state.sampleStore.samples.findIndex((s) => s === openSample);
      this.openSample(this.state.sampleStore.samples[index + 1]);
    }
  };

  openPreviousSample = () => {
    const { openSample } = this.state;
    if (openSample) {
      var index = this.state.sampleStore.samples.findIndex((s) => s === openSample);
      if (index > 0) {
        this.openSample(this.state.sampleStore.samples[index - 1]);
      }
    }
  };

  selectSample = (sample: Sample) => {
    this.props.sampleStore.selectSample(sample);
  };

  saveSamples = () => {
    this.props.handleSubmit(this.state.sampleStore.exportSamples(), this.state.reason);
  };

  // Return object where keys are the group name and values are Sample
  groupedSamples = () => {
    const { sampleStore, groupBy } = this.state;
    if (groupBy == "sample_information") return sampleStore.groupedBySampleInformation();
    if (groupBy == "no_grouping")
      return {
        "All Samples": sampleStore.samples,
      };
  };

  updateGroupBy = (groupBy: string) => {
    const { sampleStore } = this.state;
    sampleStore.groupBy = groupBy;
    this.setState({ groupBy });
  };

  renderGroupBy() {
    const { groupBy, sampleStore } = this.state;
    const grouping = sampleStore.groupedBySampleInformation();
    if (sampleStore.samples.length < 2) return null;

    return (
      <div style={{ marginTop: 0 }}>
        <span style={{ marginRight: 20 }}>
          You are editing {sampleStore.samples.length} Samples.
        </span>
        {sampleStore.samples.length > 0 &&
          [["sample_information", "Group by Attributes"], ["no_grouping", "No Grouping"]].map(
            (arr) => {
              const [value, label] = arr;
              return (
                <span key={value} style={{ marginRight: 20 }}>
                  <label>
                    <input
                      name="group"
                      checked={groupBy === value}
                      type="radio"
                      onClick={() => this.updateGroupBy(value)}
                    />
                    &nbsp; {label}
                  </label>
                </span>
              );
            }
          )}
      </div>
    );
  }

  canSubmit = () => {
    const { sampleStore, reason } = this.state;
    const { showReason, isSubmitting } = this.props;
    return (
      !isSubmitting &&
      ((showReason && reason && reason.trim()) || !showReason) &&
      sampleStore.samples.length > 0 &&
      sampleStore.samples.length > 0 &&
      sampleStore.samplesAreValid()
    );
  };

  renderPlaceholder = () => {
    return (
      <TableContainer style={{ border: "2px dashed #DDD" }}>
        <Table style={{ opacity: 0.5, width: "100%" }}>
          <Thead>
            <tr>
              <th>{I18n.t("product")}</th>
              <th>{I18n.t("suite")}</th>
              <th>{I18n.t("requested_test_date")}</th>
              <th>{I18n.t("turnaround")}</th>
              <th>Country</th>
              <th>Batch Code</th>
              <th>Supplier</th>
              <th>Farm</th>
              <th>Line Number</th>
            </tr>
          </Thead>
          <tbody>
            <tr>
              <td
                colSpan={9}
                style={{ padding: 3, paddingLeft: 6, borderBottom: "1px solid #DDD" }}
              >
                Any samples you add will appear here.
              </td>
            </tr>
            <tr>
              <td
                colSpan={9}
                style={{ padding: 3, paddingLeft: 6, borderBottom: "1px solid #DDD" }}
              >
                When browsing products, click the 'Add' button next to the product to add it to
                this registration list.
              </td>
            </tr>
            <tr>
              <td
                colSpan={9}
                style={{ padding: 3, paddingLeft: 6, borderBottom: "1px solid #DDD" }}
              >
                You can add additional samples to this list at any point during the
                registration process.
              </td>
            </tr>
          </tbody>
        </Table>
      </TableContainer>
    );
  };

  render() {
    const { sampleStore, reason, focusedSample, openSample } = this.state;
    const {
      buttonTitle,
      showReason,
      children,
      showPlaceholder,
      company_id,
      isSubmitting,
    } = this.props;
    const groupedSamples = this.groupedSamples();
    var missingDataCount = sampleStore.numberOfMissingFields();

    const theButtonTitle = isSubmitting ? "Please wait..." : buttonTitle;

    return (
      <Fragment>
        {this.renderGroupBy()}
        {sampleStore.samples.length === 0 && showPlaceholder && this.renderPlaceholder()}
        <div
          ref={this.gridRef}
          onKeyDownCapture={(e) =>
            handleAltArrowGridNavigation({
              event: e,
              root: this.gridRef && this.gridRef.current,
              closeActiveDatePicker: closeActiveDatePicker,
            })
          }
          data-sample-grid-root="true"
        >
          {_.keys(groupedSamples).map((groupFields, i) => (
            <SampleGroup
              key={i}
              groupName={groupFields}
              store={sampleStore}
              groupFields={groupedSamples}
              samples={groupedSamples[groupFields]}
              focusedSample={focusedSample}
              focusSample={this.focusSample}
              openedSample={openSample}
              openSample={this.openSample}
              openNextSample={this.openNextSample}
              openPreviousSample={this.openPreviousSample}
              companyId={company_id}
            />
          ))}
        </div>
        {showReason && (
          <div style={{ marginTop: 20, maxWidth: 400 }}>
            <label>{I18n.t("you_must_enter_a_reason_for_this_change")}</label>
            <input
              value={reason}
              onChange={(e) => this.setState({ reason: e.target.value })}
              type="text"
              className="form-control"
              placeholder={I18n.t("enter_reason")}
            />
          </div>
        )}
        <div style={{ marginTop: 10 }}>
          <div style={{ marginTop: 25 }}>
            <div style={{ display: "flex", maxWidth: 1000, alignItems: "center" }}>
              <Button
                title={theButtonTitle}
                onClick={this.saveSamples}
                primary
                disabled={!this.canSubmit()}
                style={{ marginRight: 20 }}
              />
              {children}
              {missingDataCount > 0 && (
                <Notice>
                  {I18n.t("cannot_register_samples.counting", {
                    count: missingDataCount,
                  })}
                </Notice>
              )}
            </div>
          </div>
        </div>
      </Fragment>
    );
  }
}

export default observer(SampleEditor);
